// src/utils/timeFormat.js

// Converts many formats -> "HH h MM min SS s"
// Supports:
// - "06 h 45 min 56 s"
// - "06:45" / "06:45:56"
// - "6:45 AM" / "06:45 PM" / "6:45:12 PM"
// - "06:45:56" (24h)
// Returns null if cannot parse.
export const toHHMMSS = (timeStr) => {
  if (!timeStr) return null;

  const s = String(timeStr).trim();

  // 1) "06 h 45 min 56 s" (seconds optional)
  const hmsMatch = s.match(/(\d{1,2})\s*h\s*(\d{1,2})\s*min(?:\s*(\d{1,2})\s*s)?/i);
  if (hmsMatch) {
    const hh = String(hmsMatch[1]).padStart(2, "0");
    const mm = String(hmsMatch[2]).padStart(2, "0");
    const ss = String(hmsMatch[3] ?? "0").padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }

  // 2) "HH:MM" or "HH:MM:SS" (no AM/PM)
  if (!s.includes("AM") && !s.includes("PM") && s.includes(":")) {
    const parts = s.split(":").map((p) => p.trim());
    if (parts.length === 2) {
      const [hh, mm] = parts;
      return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
    }
    if (parts.length === 3) {
      const [hh, mm, ss] = parts;
      return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    }
  }

  // 3) "6:45 AM" / "06:45 PM" / "6:45:12 PM"
  if (s.includes("AM") || s.includes("PM")) {
    // tolerate extra spaces
    const parts = s.split(" ").filter(Boolean);
    const time = parts[0];
    const mod = parts[1]; // AM / PM
    let [h, m, sec] = time.split(":");
    if (!sec) sec = "00";

    let hhNum = parseInt(h, 10);
    if (mod === "AM" && hhNum === 12) hhNum = 0;
    if (mod === "PM" && hhNum !== 12) hhNum += 12;

    return `${String(hhNum).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }

  return null;
};

export const hhmmssToHMinS = (hhmmss) => {
  if (!hhmmss) return null;
  const [hh, mm, ss] = hhmmss.split(":");
  if (!hh || !mm || ss === undefined) return null;
  return `${String(hh).padStart(2, "0")} h ${String(mm).padStart(2, "0")} min ${String(ss).padStart(2, "0")} s`;
};

// MAIN helper you will use everywhere:
export const normalizeToHMinS = (timeStr) => {
  const hhmmss = toHHMMSS(timeStr);
  if (!hhmmss) return null;
  return hhmmssToHMinS(hhmmss);
};

// If you need "now" already in the correct format:
export const nowAsHMinS = () => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return `${hh} h ${mm} min ${ss} s`;
};
